import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CollapseBlock, Spinner } from '../../../../../common/index.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import FijInterestForm from './FijInterestForm.jsx';
import { useGetInternalJobQuery } from '../../../../../../redux/services/fij_internal_jobs.jsx';
import {
  useGetInterestQuery,
  useCreateInterestMutation,
  useUpdateInterestMutation,
} from '../../../../../../redux/services/fij_applications.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { UnorderedListOutlined } from '@ant-design/icons';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import useUserHasRole from '../../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_FRI_INTERNAL_JOBS_LECTURER } from '../../../../../../utils/constants/roles.js';

const FijInterestsEdit = ({ mode, fromList }) => {
  const user = useSelector((state) => state.auth.user);

  const { id: interestId, job_id: jobId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { saveSuccess, successMessage } = useToastMessages();

  const [updateInterest, { isSuccess: interestUpdated }] =
    useUpdateInterestMutation();
  const [createInterest, { isSuccess: interestMade }] =
    useCreateInterestMutation();
  const {
    data: internalJob,
    isLoading: isLoadingInternalJob,
    refetch: refetch,
  } = mode === 'create'
    ? useGetInternalJobQuery(jobId)
    : useGetInterestQuery(interestId);

  const isFriInternalJobsLecturer = useUserHasRole(
    ROLE_FRI_INTERNAL_JOBS_LECTURER,
  );

  const initialValues = {
    internal_job_name: internalJob?.name,
    archived: internalJob?.archived_at !== null,
    mail: user?.mail,
    motivation_text: '',
    ...internalJob,
  };

  useEffect(() => {
    if (interestUpdated) {
      saveSuccess();
      navigate(routes.cmsFijJobListOfMyInterests);
      refetch();
    }
  }, [interestUpdated]);

  useEffect(() => {
    if (interestMade) {
      successMessage({
        message: t('Boli ste prihlásený na FRI prax'),
      });
      navigate(routes.cmsFijJobListOfMyInterests);
      refetch();
    }
  }, [interestMade]);

  return mode && isLoadingInternalJob ? (
    <Spinner />
  ) : (
    <>
      {mode === 'create' && !isFriInternalJobsLecturer && fromList && (
        <AppBarCMS
          title={t('Vytvoriť prihlášku na FRI prax')}
          backUrl={-1}
          backUrlTooltip={t('Späť')}
          secondaryActions={[
            {
              title: t('Zobraziť detail'),
              permission: 'internal-jobs-student',
              onClick: () => {
                navigate(`${routes.cmsFijJobDetail}/${internalJob.id}`);
              },
              icon: <UnorderedListOutlined />,
            },
          ]}
        />
      )}
      {mode === 'edit' && !isFriInternalJobsLecturer && (
        <AppBarCMS
          title={t('Moja prihláška na FRI prax')}
          backUrl={routes.cmsFijJobListOfMyInterests}
          backUrlTooltip={t('Zoznam mojich FRI praxí')}
          secondaryActions={[
            {
              title: t('Zobraziť detail'),
              permission: 'internal-jobs-student',
              onClick: () => {
                navigate(`${routes.cmsFijJobDetail}/${internalJob.id}`);
              },
              icon: <UnorderedListOutlined />,
            },
          ]}
        />
      )}
      {mode === 'edit' && isFriInternalJobsLecturer && (
        <AppBarCMS
          title={t('Prihláška študenta')}
          backUrl={-1}
          backUrlTooltip={t('Späť')}
          secondaryActions={[
            {
              title: t('Zobraziť detail'),
              permission: 'internal-jobs-student',
              onClick: () => {
                navigate(`${routes.cmsFijJobDetail}/${internalJob.id}`);
              },
              icon: <UnorderedListOutlined />,
            },
          ]}
        />
      )}
      <div style={{ margin: '10px' }}>
        {mode === 'create' && fromList && (
          <CollapseBlock>
            <FijInterestForm
              initialValues={initialValues}
              mode="create"
              isFriInternalJobsLecturer={isFriInternalJobsLecturer}
              onSubmit={(values) => {
                createInterest({ body: values, id: jobId });
              }}
            />
          </CollapseBlock>
        )}
        {mode === 'create' && !fromList && (
          <FijInterestForm
            initialValues={internalJob}
            mode="create"
            isTeacher={isFriInternalJobsLecturer}
          />
        )}
        {mode === 'edit' && (
          <>
            <CollapseBlock
              collapsible
              title={t('Názov FRI praxe')}
              defaultOpened={true}
            >
              <p>{internalJob.internal_job_name}</p>
            </CollapseBlock>
            <CollapseBlock>
              <FijInterestForm
                onSubmit={(values) => {
                  updateInterest({
                    id: interestId,
                    body: values,
                  });
                }}
                initialValues={internalJob}
                mode="edit"
                isTeacher={isFriInternalJobsLecturer}
              />
            </CollapseBlock>
          </>
        )}
      </div>
    </>
  );
};

FijInterestsEdit.propTypes = {
  mode: PropTypes.string,
  fromList: PropTypes.bool,
};

export default FijInterestsEdit;
